import { getAuth } from 'firebase/auth';

import { Injectable } from '@angular/core';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { AppCheck, AppCheckTokenResult, getToken, initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AppCheckService {
  app: FirebaseApp;
  appCheck!: AppCheck; // Using definite assignment assertion since it's initialized in constructor

  constructor() {
    this.app = initializeApp(environment.firebaseConfig);
    
    // Initialize App Check with appropriate configuration
    this.initializeAppCheckProvider();
  }

  private initializeAppCheckProvider(): void {
    try {
      // Configure debug token for development only
      if (environment.FIREBASE_APPCHECK_DEBUG_TOKEN && !environment.production) {
        console.log('🔧 App Check: Debug mode enabled for development');
        // Debug tokens should only be used in development
        (globalThis as any).FIREBASE_APPCHECK_DEBUG_TOKEN = environment.FIREBASE_APPCHECK_DEBUG_TOKEN;
      }

      // Check if we're on a known domain or if this is a temporary deployment
      const currentDomain = globalThis.location.hostname;
      const isTemporaryDomain = currentDomain.includes('.pages.dev');
      
      if (environment.production && isTemporaryDomain) {
        console.warn('🚨 App Check: Temporary deployment domain detected:', currentDomain);
        console.warn('📝 Please add this domain to your reCAPTCHA Enterprise configuration:');
        console.warn(`   Domain: ${currentDomain}`);
        console.warn(`   Site Key: ${environment.siteKey}`);
        console.warn('   Google Cloud Console: https://console.cloud.google.com/security/recaptcha');
      }

      this.appCheck = initializeAppCheck(this.app, {
        provider: new ReCaptchaEnterpriseProvider(environment.siteKey),
        isTokenAutoRefreshEnabled: true
      });

      if (environment.production) {
        console.log('🔒 App Check: Production mode - using reCAPTCHA Enterprise');
      } else {
        console.log('🔧 App Check: Development mode');
      }
    } catch (error) {
      console.error('❌ App Check initialization failed:', error);
      
      // In production, provide helpful guidance
      if (environment.production) {
        console.error('🚨 This is likely because the domain is not registered with reCAPTCHA Enterprise.');
        console.error('📝 To fix this:');
        console.error('   1. Go to Google Cloud Console → Security → reCAPTCHA Enterprise');
        console.error(`   2. Find site key: ${environment.siteKey}`);
        console.error(`   3. Add domain: ${globalThis.location.hostname}`);
        console.error('   4. Also add wildcard: *.escalas-mgmt-app.pages.dev');
      }
      
      throw error;
    }
  }

  async getToken(forceRefresh: boolean = false): Promise<AppCheckTokenResult | undefined> {
    let appCheckTokenResponse;
    try {
      console.log('🔑 Requesting App Check token...', { forceRefresh });
      appCheckTokenResponse = await getToken(this.appCheck, forceRefresh);
      console.log('✅ App Check token retrieved successfully');
      return appCheckTokenResponse;
    } catch (err) {
      console.error('❌ Failed to retrieve App Check token:', err);
      
      // Log specific error information for debugging
      if (err instanceof Error) {
        console.error('Error details:', {
          message: err.message,
          name: err.name,
          stack: err.stack
        });
        
        // Provide specific guidance for reCAPTCHA errors
        if (err.message.includes('recaptcha-error') || err.message.includes('ReCAPTCHA')) {
          console.error('🚨 reCAPTCHA Error detected:');
          console.error('   This usually means the domain is not registered with your reCAPTCHA site key.');
          console.error(`   Current domain: ${globalThis.location.hostname}`);
          console.error(`   Site key: ${environment.siteKey}`);
          console.error('   📝 Add this domain to your reCAPTCHA Enterprise configuration in Google Cloud Console.');
        }
      }
      
      // In production, we should handle this gracefully
      if (environment.production) {
        console.warn('🚨 App Check token retrieval failed in production. This may affect API access.');
        console.warn('   The app may still work for some features that don\'t require App Check.');
      }
      
      return undefined;
    }
  }

  /**
   * Check if App Check is properly configured
   */
  async validateAppCheck(): Promise<boolean> {
    try {
      const token = await this.getToken(true);
      return !!token;
    } catch (error) {
      console.error('App Check validation failed:', error);
      return false;
    }
  }

  /**
   * Get App Check configuration info for debugging
   */
  getConfigInfo(): any {
    return {
      production: environment.production,
      siteKey: environment.siteKey,
      debugEnabled: environment.FIREBASE_APPCHECK_DEBUG_TOKEN && !environment.production,
      domain: globalThis.location.hostname
    };
  }
}
